import { useState, useEffect, useMemo } from "react";
import { supabase } from '@/integrations/supabase/client';
import * as Sentry from "@sentry/react";
import { SlideAnimation } from "@/components/ui/css-animations";
import { Button } from "@/components/ui/button";
import { calculateScores } from "@/services/scoring/calculateScores";
import { syncAdvisorsToTeamMembers } from "@/services/syncAdvisorsToTeamMembers";
import { syncA8SuccessorsToCandidates } from "@/services/syncA8SuccessorsToCandidates";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { getAssessmentQuestions } from "./conversational/assessmentQuestions";
import { QuestionRenderer } from "./conversational/QuestionRenderer";
import { CompositeQuestionGroup } from "./conversational/CompositeQuestionGroup";
import { AdvisorTypeSectionCard, getAdvisorTypeFromKey } from "./conversational/AdvisorTypeSectionCard";
import { useAssessment } from "@/context/AssessmentContext";
import { AssessmentNavigation } from "./conversational/AssessmentNavigation";
import { AnswerData } from "./conversational/types";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAssessmentPersistence } from "@/hooks/useAssessmentPersistence";
import { useAuth } from "@/contexts/AuthContext";
import { AssessmentErrorBoundary } from "./AssessmentErrorBoundary";
import { InlineLoading } from "@/components/ui/loading-states";
import { useQuestionLogic } from "@/hooks/useQuestionLogic";
import { evaluateConditionalDisplay } from "@/hooks/useConditionEvaluation";
import { isSelectAnswer, QuestionAnswer } from "./conversational/answerTypes";
import { logger } from "@/utils/logger";
import { useAssessmentQuestions } from "@/hooks/useAssessmentQuestions";
import { getAssessmentIdByModuleId, getAssessmentMetaByModuleId, type AssessmentMeta } from "@/utils/assessmentLookup";
import { AssessmentIntroScreen } from "./conversational/AssessmentIntroScreen";
import { AssessmentPlaceholder } from "./AssessmentPlaceholder";

interface ConversationalAssessmentProps {
  onComplete: () => void;
  onExit: () => void;
  moduleId?: string; // Add optional moduleId prop for overlay usage
}
export function ConversationalAssessment({
  onComplete,
  onExit,
  moduleId: propModuleId
}: ConversationalAssessmentProps) {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<AnswerData>({});
  const [animationDirection, setAnimationDirection] = useState<'right' | 'left'>('right');
  const [isInitializing, setIsInitializing] = useState(true);
  const [isFinishing, setIsFinishing] = useState(false);
  const [showIntro, setShowIntro] = useState(true);
  const [assessmentMeta, setAssessmentMeta] = useState<AssessmentMeta | null>(null);
  const isMobile = useIsMobile();
  const {
    toast
  } = useToast();
  const { user } = useAuth();
  const {
    currentModuleId
  } = useAssessment();

  // Get questions based on current module (use prop moduleId if provided, otherwise context)
  const effectiveModuleId = propModuleId || currentModuleId;
  logger.debug('ConversationalAssessment effectiveModuleId', {
    effectiveModuleId
  });

  // Ensure we have a valid module ID
  if (!effectiveModuleId) {
    logger.warn('No effective module ID available, using default assessment');
  }

  // Determine if this module should use DB-driven questions
  const isDbDriven = !!effectiveModuleId && effectiveModuleId !== 'owner-readiness';

  // Resolve moduleId → assessmentId UUID for DB-driven modules
  const [resolvedAssessmentId, setResolvedAssessmentId] = useState<string | null>(null);
  useEffect(() => {
    async function resolveAssessmentId() {
      if (isDbDriven && effectiveModuleId) {
        const id = await getAssessmentIdByModuleId(effectiveModuleId);
        setResolvedAssessmentId(id);
        // Also fetch metadata for intro screen
        const meta = await getAssessmentMetaByModuleId(effectiveModuleId);
        setAssessmentMeta(meta);
      }
    }
    resolveAssessmentId();
  }, [effectiveModuleId, isDbDriven]);

  // Fetch DB questions when we have a resolved ID
  const { questions: dbQuestions, loading: dbLoading, error: dbError } = useAssessmentQuestions(
    isDbDriven ? resolvedAssessmentId : null
  );

  // Choose question source: DB for non-A1, hardcoded for A1
  const moduleQuestions = (() => {
    if (!isDbDriven) {
      return getAssessmentQuestions(effectiveModuleId || undefined);
    }
    // For DB-driven, append a generic completion screen
    if (dbQuestions.length > 0) {
      return [
        ...dbQuestions,
        {
          id: `db-assessment-completion`,
          type: 'completion' as const,
          title: 'Assessment Complete',
          required: false,
        },
      ];
    }
    return dbQuestions;
  })();

  logger.debug('Module questions loaded', {
    count: moduleQuestions.length,
    firstQuestionTitle: moduleQuestions[0]?.title,
    isDbDriven
  });
  const {
    loadLocalProgress,
    autoSave,
    manualSave,
    saveToDatabase,
    saveAllResponses,
    clearProgress,
    updateAssessmentData,
    isSaving,
    teamId,
    assessmentId
  } = useAssessmentPersistence(
    effectiveModuleId || 'snapshot-assessment',
    isDbDriven ? resolvedAssessmentId || undefined : undefined
  );
  const {
    getVisibleQuestions
  } = useQuestionLogic(moduleQuestions, answers);

  // Load saved progress on mount
  useEffect(() => {
    const savedProgress = loadLocalProgress();
    if (savedProgress) {
      setCurrentQuestionIndex(savedProgress.questionIndex);
      setAnswers(savedProgress.answers);
      toast({
        title: "Progress restored",
        description: "Your previous assessment progress has been restored."
      });
    }
    setIsInitializing(false);
  }, [loadLocalProgress]);
  const visibleQuestionsRaw = getVisibleQuestions();

  // Filter out credentials questions (Change 2)
  const visibleQuestions = useMemo(
    () => visibleQuestionsRaw.filter((q) => !/_credentials$/.test(q.id)),
    [visibleQuestionsRaw]
  );

  // Build "screen list": group questions with the same question_group into one screen entry.
  // For A6 advisor-type questions, group all questions for the same advisor prefix into one screen.
  const screenList = useMemo(() => {
    const screens: Array<{ key: string; questions: typeof visibleQuestions; advisorType?: string }> = [];
    const seenGroups = new Set<string>();
    const seenAdvisorTypes = new Set<string>();

    for (const q of visibleQuestions) {
      // Check if this is an advisor-type question (A6 pattern)
      const advisorType = getAdvisorTypeFromKey(q.id);
      if (advisorType) {
        if (!seenAdvisorTypes.has(advisorType)) {
          seenAdvisorTypes.add(advisorType);
          const advisorQuestions = visibleQuestions.filter(
            (vq) => getAdvisorTypeFromKey(vq.id) === advisorType
          );
          screens.push({ key: `advisor:${advisorType}`, questions: advisorQuestions, advisorType });
        }
        continue;
      }

      const group = (q as any)._questionGroup as string | null | undefined;
      if (group) {
        if (!seenGroups.has(group)) {
          seenGroups.add(group);
          const groupQuestions = visibleQuestions.filter(
            (vq) => (vq as any)._questionGroup === group
          );
          screens.push({ key: `group:${group}`, questions: groupQuestions });
        }
      } else {
        screens.push({ key: q.id, questions: [q] });
      }
    }
    return screens;
  }, [visibleQuestions]);

  const currentScreen = screenList[currentQuestionIndex];
  const currentQuestion = currentScreen?.questions[0];



  logger.debug('ConversationalAssessment state', {
    currentQuestionIndex,
    screensLength: screenList.length,
    currentQuestionId: currentQuestion?.id,
    currentQuestionType: currentQuestion?.type,
    moduleId: effectiveModuleId
  });

  // Exclude welcome, level-set, completion, and header screens from the progress calculation
  const totalScreens = screenList.length;
  const excludedScreens = screenList.filter(s => {
    const t = s.questions[0]?.type;
    return t === 'welcome' || t === 'completion' || t === 'level-set' || t === 'header';
  }).length;
  const contentScreens = totalScreens - excludedScreens;

  // Calculate progress percentage
  const excludedScreensBeforeCurrent = screenList.slice(0, currentQuestionIndex).filter(s => {
    const t = s.questions[0]?.type;
    return t === 'welcome' || t === 'level-set' || t === 'header';
  }).length;
  const contentScreenIndex = currentQuestionIndex - excludedScreensBeforeCurrent;
  const progressPercentage = contentScreens > 0 ? (contentScreenIndex / contentScreens) * 100 : 0;
  const isLastContentQuestion = currentQuestionIndex === totalScreens - 2;


  const handleNext = async () => {
    // For composite groups, validate all required questions on the current screen
    const screenQuestions = currentScreen?.questions ?? [currentQuestion];
    const unansweredRequired = screenQuestions.find(
      (q: any) => q.required && !answers[q.id] && q.type !== 'welcome' && q.type !== 'completion'
    );
    if (unansweredRequired) {
      toast({
        title: "Answer required",
        description: "Please answer all required questions before continuing",
        variant: "destructive"
      });
      return;
    }


    // If this is the last content question ("Finish" button), run handleFinish BEFORE advancing
    if (isLastContentQuestion) {
      if (isFinishing) return; // Prevent double-click
      setIsFinishing(true);
      try {
        await handleFinish();
      } finally {
        setIsFinishing(false);
      }
    } else {
      // Save to database when navigating
      await saveToDatabase(currentQuestionIndex, answers);
    }

    setAnimationDirection('right');
    if (currentQuestionIndex < totalScreens - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      window.scrollTo(0, 0);
    }
  };
  const handlePrevious = () => {
    setAnimationDirection('left');
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      window.scrollTo(0, 0);
    }
  };
  const handleSave = async () => {
    await manualSave(currentQuestionIndex, answers);
  };
  const handleFinish = async () => {
    const userId = user?.id;
    const moduleIdForDb = effectiveModuleId || 'owner-readiness';
    
    console.log('[Assessment] handleFinish called. userId:', userId, 'moduleId:', moduleIdForDb, 'assessmentId:', assessmentId);

    // Save final state to database (assessment_progress blob)
    await saveToDatabase(currentQuestionIndex, answers);
    // Ensure individual responses are committed before sync reads them
    await saveAllResponses(answers);
    clearProgress();
    
    // 1. Mark module as complete FIRST (before scoring)
    if (userId) {
      const { error: moduleError } = await supabase
        .from('assessment_modules')
        .upsert({
          user_id: userId,
          module_id: moduleIdForDb,
          status: 'complete',
          completion_date: new Date().toISOString(),
          metadata: {}
        } as never, {
          onConflict: 'user_id,module_id'
        });

      if (moduleError) {
        console.error('[Assessment] Failed to update assessment_modules:', moduleError);
      } else {
        console.log('[Assessment] Module marked complete:', moduleIdForDb);
      }
    }
    
    // 2. Run scoring (non-blocking, don't let it break completion)
    if (userId && assessmentId) {
      try {
        const { overallScore, success } = await calculateScores(userId, teamId, assessmentId);
        
        if (success) {
          console.log(`[Scoring] mySuccession Score: ${overallScore}%`);
          await supabase
            .from('assessment_modules')
            .update({ 
              final_score: overallScore,
              metadata: { scoring_success: true, scored_at: new Date().toISOString() }
            } as never)
            .eq('user_id', userId)
            .eq('module_id', moduleIdForDb);
        } else {
          console.warn('[Scoring] Calculation completed but returned success=false');
          await supabase
            .from('assessment_modules')
            .update({ 
              metadata: { scoring_success: false, scored_at: new Date().toISOString() }
            } as never)
            .eq('user_id', userId)
            .eq('module_id', moduleIdForDb);
        }
      } catch (error) {
        console.error('[Assessment] Scoring failed:', error);
        await supabase
          .from('assessment_modules')
          .update({ 
            metadata: { scoring_success: false, error: String(error) }
          } as never)
          .eq('user_id', userId)
          .eq('module_id', moduleIdForDb);
      }
    }

    // 3. Sync A6 advisors to team_members (non-blocking)
    if (userId && moduleIdForDb === 'advisory-team' && teamId) {
      try {
        await syncAdvisorsToTeamMembers(userId, teamId);
      } catch (err) {
        console.error('[Assessment] A6 advisor sync failed:', err);
      }
    }

    // 4. Sync A8 successors to successor_candidates (non-blocking)
    if (userId && moduleIdForDb === 'successor-strategy' && teamId) {
      try {
        await syncA8SuccessorsToCandidates(userId, teamId);
      } catch (err) {
        console.error('[Assessment] A8 successor sync failed:', err);
      }
    }

    toast({
      title: "Assessment completed!",
      description: "Your assessment has been completed and saved."
    });
  };
  const handleAnswerChange = (questionId: string, value: QuestionAnswer) => {
    const newAnswers = {
      ...answers,
      [questionId]: value
    };
    
    // Clear stale answers for conditional questions that are no longer visible
    // and auto-save default values for hidden questions that define one
    const updatedVisible = moduleQuestions.filter(q => {
      if (!q.conditionalDisplay) return true;
      return evaluateConditionalDisplay(q.conditionalDisplay, newAnswers);
    });
    const visibleIds = new Set(updatedVisible.map(q => q.id));
    for (const q of moduleQuestions) {
      if (q.conditionalDisplay && !visibleIds.has(q.id)) {
        if ('skipDefaultValue' in q && q.skipDefaultValue !== undefined) {
          // Auto-save default value for hidden question
          newAnswers[q.id] = q.skipDefaultValue;
        } else {
          // Clear stale answer
          delete newAnswers[q.id];
        }
      }
    }
    
    setAnswers(newAnswers);

    // Auto-save after each answer (also updates sendBeacon refs)
    autoSave(currentQuestionIndex, newAnswers);
  };

  // Animation variants
  const variants = {
    enter: (direction: 'right' | 'left') => ({
      x: direction === 'right' ? 100 : -100,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction: 'right' | 'left') => ({
      x: direction === 'right' ? -100 : 100,
      opacity: 0
    })
  };

  // Show loading state while initializing, loading DB questions, or waiting for assessment metadata
  if (isInitializing || (isDbDriven && (dbLoading || !assessmentMeta))) {
    return <div className="h-full flex items-center justify-center">
        <InlineLoading size="lg" text="Loading assessment..." />
      </div>;
  }

  // Show placeholder for DB-driven assessments with zero questions
  if (isDbDriven && !dbLoading && !dbError && dbQuestions.length === 0) {
    return <AssessmentPlaceholder moduleId={effectiveModuleId} onExit={onExit} />;
  }

  // Show error state for DB-driven assessments
  if (isDbDriven && dbError) {
    return <div className="h-full flex items-center justify-center">
        <div className="text-center max-w-md">
          <h3 className="text-lg font-semibold mb-2">Failed to load assessment</h3>
          <p className="text-muted-foreground mb-4">{dbError}</p>
          <Button onClick={onExit} variant="outline">Go Back</Button>
        </div>
      </div>;
  }

  // Show intro screen for DB-driven assessments before the first question
  if (isDbDriven && showIntro && assessmentMeta) {
    return (
      <div className="h-full flex flex-col">
        <div className="flex-1 flex items-center justify-center px-4">
          <AssessmentIntroScreen
            meta={assessmentMeta}
            onBegin={() => setShowIntro(false)}
          />
        </div>
      </div>
    );
  }

  // Handle missing question gracefully
  if (!currentQuestion) {
    Sentry.captureException(new Error('Current question is undefined'), {
      tags: {
        context: 'assessment_error',
        component: 'ConversationalAssessment'
      },
      extra: {
        message: 'Current question is undefined',
        currentQuestionIndex,
        visibleQuestionsLength: visibleQuestions?.length,
        moduleId: effectiveModuleId,
        answers
      }
    });
    return <div className="h-full flex items-center justify-center">
        <div className="text-center max-w-md">
          <h3 className="text-lg font-semibold mb-2">Question not found</h3>
          <p className="text-muted-foreground mb-4">
            Unable to load question {currentQuestionIndex + 1} of {visibleQuestions?.length || 0} for module: {effectiveModuleId}
          </p>
          <div className="space-x-2">
            <Button onClick={() => setCurrentQuestionIndex(Math.max(0, currentQuestionIndex - 1))} variant="outline">
              Go Back
            </Button>
            <Button onClick={() => setCurrentQuestionIndex(0)} variant="default">
              Restart Assessment
            </Button>
          </div>
        </div>
      </div>;
  }

  // Show section badge if it exists
  const isWelcomeScreen = currentQuestion.type === 'welcome';
  const isCompletionScreen = currentQuestion.type === 'completion';
  const isLevelSetScreen = currentQuestion.type === 'level-set';
  const isContentScreen = !isWelcomeScreen && !isCompletionScreen && !isLevelSetScreen;
  const handleRetry = () => {
    // Reset to beginning if error occurs
    setCurrentQuestionIndex(0);
    setAnswers({});
  };
  return <AssessmentErrorBoundary onRetry={handleRetry}>
      <div className="h-full flex flex-col max-w-full">
        {/* Progress bar - only show for non-welcome/completion screens */}
        {isContentScreen && <div className="mb-4 sm:mb-6 px-2 sm:px-4">
            <div className="flex justify-between items-center text-sm text-muted-foreground mb-2 my-[20px]">
              <span>{Math.round(progressPercentage)}% complete</span>
              {isSaving && <InlineLoading size="sm" text="Saving..." />}
            </div>
            <Progress value={progressPercentage} className="h-2" />
          </div>}
        
        {/* Main content area - flexible to take remaining space */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <Card className={`transition-all duration-300 flex-1 flex flex-col overflow-y-auto
            ${isWelcomeScreen ? 'bg-onboarding-gradient' : 'bg-transparent'}
            shadow-none border-none`}>
            <CardContent className={`flex-1 flex flex-col ${isContentScreen ? 'pt-2' : 'pt-4'} px-4 sm:px-6 ${isMobile ? 'pb-4' : 'pb-6'}`}>
              {/* Question content with CSS animation */}
              <SlideAnimation direction={animationDirection} className="flex items-center justify-center w-full flex-1">
                {currentScreen && (currentScreen as any).advisorType
                  ? <AdvisorTypeSectionCard
                      advisorType={(currentScreen as any).advisorType}
                      questions={currentScreen.questions as any}
                      answers={answers}
                      onAnswerChange={handleAnswerChange}
                      onNext={handleNext}
                      onFinish={onComplete}
                    />
                  : currentScreen && currentScreen.questions.length > 1
                    ? <CompositeQuestionGroup
                        questions={currentScreen.questions as any}
                        answers={answers}
                        onAnswerChange={handleAnswerChange}
                        onNext={handleNext}
                        onFinish={onComplete}
                      />
                    : <QuestionRenderer question={currentQuestion} answers={answers} onAnswerChange={handleAnswerChange} onNext={handleNext} onFinish={onComplete} />
                }
              </SlideAnimation>
            </CardContent>
          </Card>
        </div>
        
        {/* Navigation at bottom - only show for non-welcome/completion screens */}
        {isContentScreen && <div className="mt-auto border-t bg-background px-4 py-3 sm:py-4">
            <AssessmentNavigation onPrevious={handlePrevious} onNext={handleNext} onSave={handleSave} isPreviousDisabled={currentQuestionIndex === 0} isLastQuestion={currentQuestionIndex === totalScreens - 2} isSaving={isSaving} isFinishing={isFinishing} />
          </div>}

      </div>
    </AssessmentErrorBoundary>;
}